import { type FrameworkChangeItem } from '@/hooks/useFrameworkChanges';
import { cn } from '@/lib/utils';

export function ChangeItemRow({ item }: { item: FrameworkChangeItem }) {
  // Render a single change item row with type-coloured badge + entity name
  const before = item.beforeJson ? JSON.parse(item.beforeJson) : null;
  const after = item.afterJson ? JSON.parse(item.afterJson) : null;
  // For allocation items, show activity ID; for start_week, show capability ID
  const name = item.entityType === 'allocation'
    ? `Activity: ${(after?.activityId || item.entityId || '(unknown)') as string}`
    : item.entityType === 'start_week'
      ? `Capability: ${(after?.capabilityId || item.entityId || '(unknown)') as string}`
      : (after?.name || before?.name || item.entityId || '(unnamed)') as string;

  const colour =
    item.changeType === 'create'
      ? 'border-green-300 bg-green-50'
      : item.changeType === 'update'
        ? 'border-amber-300 bg-amber-50'
        : 'border-red-300 bg-red-50';

  const badgeColour =
    item.changeType === 'create'
      ? 'bg-green-200 text-green-900'
      : item.changeType === 'update'
        ? 'bg-amber-200 text-amber-900'
        : 'bg-red-200 text-red-900';

  return (
    <div className={cn('rounded border px-3 py-2', colour)}>
      <div className="flex items-center gap-2">
        <span className={cn('inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase', badgeColour)}>
          {item.changeType}
        </span>
        <span className="text-[10px] text-gray-600 uppercase">{item.entityType}</span>
        <span className={cn('text-sm font-medium text-gray-900 truncate', item.changeType === 'delete' && 'line-through text-red-700')}>
          {name}
        </span>
      </div>
      {/* Allocation change: show week summary */}
      {item.entityType === 'allocation' && after?.weekAllocations && (
        <div className="mt-1.5 text-[11px] text-gray-700 font-mono">
          {Object.entries(after.weekAllocations as Record<string, number>)
            .filter(([, d]) => d > 0)
            .slice(0, 8)
            .map(([w, d]) => `W${w}:${d}`)
            .join(' · ')}
        </div>
      )}
      {/* Start week change */}
      {item.entityType === 'start_week' && after?.startWeek != null && (
        <div className="mt-1.5 text-[11px] text-gray-700 font-mono">
          Start week: W{after.startWeek as number}
        </div>
      )}
      {/* Standard field diff for other entity types */}
      {item.changeType === 'update' && item.entityType !== 'allocation' && item.entityType !== 'start_week' && before && after && (
        <div className="mt-1.5 text-[11px] text-gray-700 space-y-0.5">
          {Object.keys(after).filter(k => k !== 'id' && before[k] !== after[k]).slice(0, 5).map(k => (
            <div key={k} className="font-mono">
              <span className="text-gray-500">{k}:</span>{' '}
              <span className="text-red-700 line-through">{String(before[k] ?? '\u2205')}</span>
              {' \u2192 '}
              <span className="text-green-700">{String(after[k] ?? '\u2205')}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
